import { useState, useMemo, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Sidebar, type SidebarMenuItem, type SidebarSubMenuItem } from './Sidebar';
import { TopNav } from './TopNav';
import Breadcrumbs from '@components/shared/Breadcrumbs/Breadcrumbs';
import NotificationContainer from '@components/shared/NotificationContainer/NotificationContainer';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import {
  incrementUnreadCountInCache,
  prependNotificationToCache,
} from '@/hooks/useNotifications';
import { getEffectiveNavigation } from '@/services/navigationService';
import { queryClient } from '@/lib/queryClient';
import { getRouteForModule } from '@/lib/navigationRegistry';
import type { NavigationEntry } from '@/types/navigation';
import type { OrganizationType } from '@/types/session';

// ============================================
// App Shell — Sidebar + TopNav + Content area
// Uses global layout classes from _layout.scss
// ============================================

// ── Icon Components ──

function PeopleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function ConfigurationIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
      <path
        d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.988C9.5799 19.718 9.31074 19.5115 9 19.395C8.69838 19.2619 8.36381 19.2222 8.03941 19.281C7.71502 19.3398 7.41568 19.4945 7.18 19.725L7.12 19.785C6.93425 19.971 6.71368 20.1185 6.47088 20.2191C6.22808 20.3198 5.96783 20.3716 5.705 20.3716C5.44217 20.3716 5.18192 20.3198 4.93912 20.2191C4.69632 20.1185 4.47575 19.971 4.29 19.785C4.10405 19.5993 3.95653 19.3787 3.85588 19.1359C3.75523 18.8931 3.70343 18.6328 3.70343 18.37C3.70343 18.1072 3.75523 17.8469 3.85588 17.6041C3.95653 17.3613 4.10405 17.1407 4.29 16.955L4.35 16.895C4.58054 16.6593 4.73519 16.36 4.794 16.0356C4.85282 15.7112 4.81312 15.3766 4.68 15.075C4.55324 14.7792 4.34276 14.527 4.07447 14.3493C3.80618 14.1716 3.49179 14.0763 3.17 14.075H3C2.46957 14.075 1.96086 13.8643 1.58579 13.4892C1.21071 13.1141 1 12.6054 1 12.075C1 11.5446 1.21071 11.0359 1.58579 10.6608C1.96086 10.2857 2.46957 10.075 3 10.075H3.09C3.42099 10.0673 3.742 9.96012 4.012 9.76751C4.282 9.5749 4.4885 9.30574 4.605 8.995C4.73812 8.69338 4.77782 8.35881 4.719 8.03441C4.66019 7.71002 4.50554 7.41068 4.275 7.175L4.215 7.115C4.02905 6.92925 3.88153 6.70868 3.78088 6.46588C3.68023 6.22308 3.62843 5.96283 3.62843 5.7C3.62843 5.43717 3.68023 5.17692 3.78088 4.93412C3.88153 4.69132 4.02905 4.47075 4.215 4.285C4.40075 4.09905 4.62132 3.95153 4.86412 3.85088C5.10692 3.75023 5.36717 3.69843 5.63 3.69843C5.89283 3.69843 6.15308 3.75023 6.39588 3.85088C6.63868 3.95153 6.85925 4.09905 7.045 4.285L7.105 4.345C7.34068 4.57554 7.64002 4.73019 7.96441 4.789C8.28881 4.84782 8.62338 4.80812 8.925 4.675H9C9.29577 4.54824 9.54802 4.33776 9.72569 4.06947C9.90337 3.80118 9.99872 3.48679 10 3.165V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
      <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EntitiesIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
      <path
        d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function OrgProfileIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 21H21M4 21V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H14C14.5304 4 15.0391 4.21071 15.4142 4.58579C15.7893 4.96086 16 5.46957 16 6V21M20 21V11C20 10.4696 19.7893 9.96086 19.4142 9.58579C19.0391 9.21071 18.5304 9 18 9H16M8 9H12M8 13H12M8 17H12"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

// Backend `icon` field (e.g. "settings", "people", "folder") → React node.
// Falls back to identifier-based resolution, then to the people icon.
const API_ICONS: Record<string, ReactNode> = {
  settings: <ConfigurationIcon />,
  people: <PeopleIcon />,
  briefcase: <EntitiesIcon />,
  building: <OrgProfileIcon />,
  home: <DashboardIcon />,
  folder: <FolderIcon />,
};

const IDENTIFIER_ICONS: Record<string, ReactNode> = {
  CATEGORY_CONFIGURATION: <ConfigurationIcon />,
  CATEGORY_EMPLOYEE_DOCUMENTS: <FolderIcon />,
  MODULE_TENANT_MANAGEMENT: <PeopleIcon />,
  MODULE_ENTITY_MANAGEMENT: <EntitiesIcon />,
  MODULE_EMPLOYEES: <PeopleIcon />,
  MODULE_TENANT_PROFILE: <OrgProfileIcon />,
};

function resolveIcon(identifier: string, apiIcon?: string): ReactNode {
  if (apiIcon && API_ICONS[apiIcon]) return API_ICONS[apiIcon];
  return IDENTIFIER_ICONS[identifier] ?? <PeopleIcon />;
}

// ── Build sidebar items from navigation API ──

type TFn = (key: string, fallback: string) => string;

function buildMenuItemsFromNav(
  entries: NavigationEntry[],
  orgType: OrganizationType,
  t: TFn,
): SidebarMenuItem[] {
  const items: SidebarMenuItem[] = [];

  for (const entry of entries) {
    if (entry.type === 'GROUP') {
      const children: SidebarSubMenuItem[] = [];
      for (const child of entry.items) {
        const href = getRouteForModule(child.moduleIdentifier, orgType);
        if (!href) continue;
        children.push({
          id: child.moduleIdentifier.toLowerCase(),
          label: t(child.displayKey, child.displayKey),
          href,
        });
      }
      if (children.length > 0) {
        items.push({
          id: entry.identifier.toLowerCase(),
          label: t(entry.displayKey, entry.displayKey),
          icon: resolveIcon(entry.identifier, entry.icon),
          children,
        });
      }
    } else {
      const href = getRouteForModule(entry.identifier, orgType);
      if (!href) continue;
      items.push({
        id: entry.identifier.toLowerCase(),
        label: t(entry.displayKey, entry.displayKey),
        icon: resolveIcon(entry.identifier, entry.icon),
        href,
      });
    }
  }

  return items;
}

// ── Fallback: static sidebar items (used when API hasn't loaded yet) ──

function getStaticMenuItems(orgType: OrganizationType | null, userType: string | null | undefined, t: TFn): SidebarMenuItem[] {
  if (userType === 'EMPLOYEE') {
    return [{ id: 'home', label: t('K_SIDEBAR_HOME', 'Home'), icon: <DashboardIcon />, href: '/e' }];
  }
  switch (orgType) {
    case 'WORKPAY':
      return [
        {
          id: 'category_configuration',
          label: t('K_SIDEBAR_CONFIGURATION', 'Configuration'),
          icon: <ConfigurationIcon />,
          children: [
            { id: 'module_employment_types', label: t('K_SIDEBAR_EMPLOYMENT_TYPES', 'Employment Types'), href: '/w/employment-types' },
            { id: 'module_forms', label: t('K_SIDEBAR_FORMS', 'Forms'), href: '/w/form' },
            { id: 'module_tenant_templates', label: t('K_SIDEBAR_TENANT_TEMPLATES', 'Client Onboarding Templates'), href: '/w/tenant-templates' },
            { id: 'module_entity_templates', label: t('K_SIDEBAR_ENTITY_TEMPLATES', 'Legal Entity Templates'), href: '/w/entity-templates' },
            { id: 'module_employee_templates', label: t('K_SIDEBAR_EMPLOYEE_TEMPLATES', 'Employee Templates'), href: '/w/employee-templates' },
            { id: 'module_product_types', label: t('K_LABEL_PRODUCT_TYPES', 'Product Types'), href: '/w/product-types' },
            { id: 'module_module_registry', label: t('K_MODULE_REGISTRY', 'Module Registry'), href: '/w/modules' },
            { id: 'module_role_management', label: t('K_SIDEBAR_ROLES', 'Roles'), href: '/w/roles' },
            { id: 'module_user_management', label: t('K_SIDEBAR_USERS', 'Users'), href: '/w/users' },
            { id: 'module_contract_templates', label: t('K_SIDEBAR_CONTRACT_TEMPLATES', 'Contract Templates'), href: '/w/document-templates' },
          ],
        },
        { id: 'module_tenant_management', label: t('K_SIDEBAR_TENANTS', 'Clients'), icon: <PeopleIcon />, href: '/w/dashboard' },
        { id: 'module_entity_management', label: t('K_SIDEBAR_ENTITIES', 'Legal Entities'), icon: <EntitiesIcon />, href: '/w/entities' },
        { id: 'module_employees', label: t('K_SIDEBAR_EMPLOYEES', 'Employees'), icon: <PeopleIcon />, href: '/w/employees' },
        { id: 'module_document_snapshots', label: t('K_SIDEBAR_CONTRACTS', 'Contracts'), icon: <EntitiesIcon />, href: '/w/contracts' },
      ];
    case 'TENANT':
      return [
        { id: 'module_document_snapshots', label: t('K_SIDEBAR_CONTRACTS', 'Contracts'), icon: <EntitiesIcon />, href: '/t/contracts' },
        { id: 'module_signing_requests', label: t('K_SIDEBAR_SIGNING_REQUESTS', 'Signing Requests'), icon: <EntitiesIcon />, href: '/t/signing-requests' },
        {
          id: 'category_configuration',
          label: t('K_SIDEBAR_CONFIGURATION', 'Configuration'),
          icon: <ConfigurationIcon />,
          children: [
            { id: 'module_role_management', label: t('K_SIDEBAR_ROLES', 'Roles'), href: '/t/roles' },
            { id: 'module_user_management', label: t('K_SIDEBAR_USERS', 'Users'), href: '/t/users' },
          ],
        },
        { id: 'module_employees', label: t('K_SIDEBAR_EMPLOYEES', 'Employees'), icon: <PeopleIcon />, href: '/t/employees' },
      ];
    case 'PARTNER':
      return [{ id: 'home', label: t('K_SIDEBAR_HOME', 'Home'), icon: <DashboardIcon />, href: '/p' }];
    default:
      return [];
  }
}

// ── Active item resolver ──

function getActiveItemFromPath(pathname: string, menuItems: SidebarMenuItem[], bottomItems?: SidebarMenuItem[]): string {
  const allItems = [...menuItems, ...(bottomItems ?? [])];

  // Check children first (more specific match)
  for (const item of allItems) {
    if (item.children) {
      for (const child of item.children) {
        if (pathname.startsWith(child.href)) return child.id;
      }
    }
  }
  // Then top-level items
  for (const item of allItems) {
    if (item.href && pathname.startsWith(item.href)) return item.id;
  }

  return allItems[0]?.id ?? '';
}

// ── Bottom items: prefer API `sidebar-bottom`, fall back to hardcoded list ──

function getTenantBottomItems(t: TFn): SidebarMenuItem[] {
  return [
    { id: 'org-profile', label: t('K_SIDEBAR_ORGANIZATION_PROFILE', 'Company Profile'), icon: <OrgProfileIcon />, href: '/t/profile/self' },
  ];
}

// ============================================
// COMPONENT
// ============================================

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const organizationType = useAuthStore((s) => s.organizationType);
  const userType = useAuthStore((s) => s.userType);
  const session = useAuthStore((s) => s.session);
  const { t } = useTranslation();

  const isEmployee = userType === 'EMPLOYEE' || pathname.startsWith('/e');

  // Fetch navigation from API
  const { data: effectiveNav } = useQuery({
    queryKey: ['effectiveNavigation'],
    queryFn: () => getEffectiveNavigation(),
    enabled: !!session && !isEmployee,
  });

  // SSE real-time notifications.
  // Inject the new row into the infinite query cache and bump the unread-count
  // cache directly — avoids refetching every loaded page and keeps the badge
  // in sync without a store mirror. The cache is the single source of truth.
  useNotificationStream({
    enabled: !!session,
    onNotification: (notification) => {
      prependNotificationToCache(queryClient, notification);
      incrementUnreadCountInCache(queryClient);

      useNotificationStore.getState().addNotification({
        variant: 'informational',
        title: notification.title,
        description: notification.body,
      });
    },
  });

  // Render whatever the effective navigation API returns. No client-side
  // permission filtering — the API is authoritative. Route guards in
  // routes/w.tsx and routes/t.tsx still block URL access for unauthorized users.
  const menuItems = useMemo(() => {
    if (!organizationType) return [];

    const sidebarTopEntries = effectiveNav?.['sidebar-top'];
    if (sidebarTopEntries && sidebarTopEntries.length > 0 && organizationType !== 'PARTNER') {
      return buildMenuItemsFromNav(sidebarTopEntries, organizationType, t);
    }
    return getStaticMenuItems(organizationType, isEmployee ? 'EMPLOYEE' : userType, t);
  }, [effectiveNav, organizationType, isEmployee, userType, t]);

  // Bottom items: prefer the `sidebar-bottom` section from the navigation API.
  // If the API returns it (e.g. MODULE_TENANT_PROFILE seeded in V1__module_config_seed.js),
  // render it through the same builder used for `sidebar-top`. If absent, fall back to the
  // hardcoded tenant-only list so the Organization Profile entry never disappears mid-rollout.
  const bottomItems = useMemo(() => {
    if (!organizationType || isEmployee) return undefined;
    const apiBottom = effectiveNav?.['sidebar-bottom'];
    if (apiBottom && apiBottom.length > 0) {
      const built = buildMenuItemsFromNav(apiBottom, organizationType, t);
      if (built.length > 0) return built;
    }
    if (organizationType === 'TENANT') return getTenantBottomItems(t);
    return undefined;
  }, [effectiveNav, organizationType, isEmployee, t]);

  const activeItemId = getActiveItemFromPath(pathname, menuItems, bottomItems);

  return (
    <div className="app-shell">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((prev) => !prev)}
        menuItems={menuItems}
        activeItemId={activeItemId}
        bottomItems={bottomItems}
      />

      <div className="sidebar-overlay" />

      <TopNav collapsed={sidebarCollapsed} />

      <main
        className="main-content"
        style={sidebarCollapsed ? { marginLeft: '72px' } : { marginLeft: '216px' }}
      >
        <Breadcrumbs />
        {children}
      </main>

      <NotificationContainer />
    </div>
  );
}
